// Hook that returns an access-token getter. Centralises the MSAL silent
// acquisition + interactive fallback dance so pages don't reimplement it.
//
// Usage:
//   const getToken = useApiToken();
//   const { projects } = await api.listProjects(await getToken());

import { useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { apiScopes } from './msalConfig';
import { arrivalLoginHint, signedInAccount } from './ssoMemory';
import { linkLoginHint } from './linkHint';

export function useApiToken(): () => Promise<string> {
  const { instance, accounts } = useMsal();
  return useCallback(async () => {
    // The ACTIVE account — the link's person, or whoever just signed in —
    // never simply the first cached (auth/ssoMemory.ts).
    const account = signedInAccount(instance, accounts);
    if (!account) {
      // No signed-in account — initiate sign-in via redirect, naming who, so
      // Entra signs them in from their session instead of showing a picker
      // (every link lands signed in, auth/ssoMemory.ts).
      const hint = arrivalLoginHint(linkLoginHint(window.location.search, window.location.hash));
      await instance.loginRedirect({ scopes: apiScopes, ...(hint ? { loginHint: hint } : {}) });
      throw new Error('Sign-in redirect initiated');
    }
    try {
      const result = await instance.acquireTokenSilent({
        account,
        scopes: apiScopes,
      });
      return result.accessToken;
    } catch (err) {
      // Common path: AAD requires fresh consent or token is unrecoverable.
      // Fall through to interactive redirect.
      if (err instanceof InteractionRequiredAuthError) {
        await instance.acquireTokenRedirect({ account, scopes: apiScopes });
        throw new Error('Interactive token acquisition redirect initiated');
      }
      throw err;
    }
  }, [instance, accounts]);
}
